<template>
  <Modal :active="active" :vehicle="vehicle" :title="$t('forms.buy.title')" class="buyModal" @modalClosed="closeModal" ref="modal">
    <div class="form-modal-intro">
      <div v-if="introText" v-html="introText"></div>
    </div>
    <div v-if="currentStep < 4" class="progressBar">
      <div
        class="progressBar__step -clickable"
        v-on:click="toStep(1, true)"
        :class="{ '-active': currentStep === 1, '-passed': currentStep > 1 }"
      >
        {{ $t('forms.buy.stepPersonalDetails') }}
      </div>
      <div class="progressBar__step" v-on:click="toStep(2, true)" :class="{ '-active': currentStep === 2, '-passed': currentStep > 2 }">
        {{ $t('forms.buy.stepPaymentDelivery') }}
      </div>
      <div class="progressBar__step" :class="{ '-active': currentStep === 3, '-passed': currentStep > 3 }">
        {{ $t('forms.buy.stepConfirm') }}
      </div>
    </div>
    <!-- .buyModal__progress -->

    <form id="buy-vehicle" @submit.prevent="submitForm($event)">
      <div v-if="currentStep === 1" class="buyModal__page page-1">
        <p class="buyModal__page__description">{{ $t('forms.buy.personalDetailsDesc') }}</p>

        <p v-if="errors[1].length > 0" class="form-error">{{ $t('forms.errors.requiredFields') }}</p>

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label">{{ $t('forms.buy.consumerOrBusiness') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            <div class="form-radio form-radio--inline">
              <input id="buy-orderType-1" class="form-radio__input" type="radio" value="Particulier" v-model="orderType" />
              <label class="form-radio__label" for="buy-orderType-1">{{ $t('forms.buy.consumer') }}</label>
            </div>
            <!-- .form-radio -->

            <div class="form-radio form-radio--inline">
              <input id="buy-orderType-2" class="form-radio__input" type="radio" value="Zakelijk" v-model="orderType" />
              <label class="form-radio__label" for="buy-orderType-2">{{ $t('forms.buy.business') }}</label>
            </div>
            <!-- .form-radio -->
            <p v-if="isError('orderType')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label">{{ $t('forms.fields.salutation') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--radio">
            <div class="form-radio form-radio--inline">
              <input id="buy-salutation-1" class="form-radio__input" type="radio" value="Dhr." v-model="salutation" />
              <label class="form-radio__label" for="buy-salutation-1">{{ $t('forms.fields.salutationMale') }}</label>
            </div>
            <!-- .form-radio -->

            <div class="form-radio form-radio--inline">
              <input id="buy-salutation-2" class="form-radio__input" type="radio" value="Mevr." v-model="salutation" />
              <label class="form-radio__label" for="buy-salutation-2">{{ $t('forms.fields.salutationFemale') }}</label>
            </div>
            <!-- .form-radio -->

            <p v-if="isError('salutation')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label" for="buy-fname">{{ $t('forms.fields.name') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field">
            <div class="formRow__field__grid">
              <div class="formRow__field__grid__column">
                <input
                  id="buy-fname"
                  class="form-textField"
                  :class="{ 'form-textField--error': isError('fname') }"
                  type="text"
                  v-model="fname"
                  :placeholder="$t('forms.fields.firstName')"
                />
                <p v-if="isError('fname')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
              </div>
              <!-- .formRow__field__grid__column -->
              <div class="formRow__field__grid__column">
                <input
                  id="buy-lname"
                  class="form-textField"
                  :class="{ 'form-textField--error': isError('lname') }"
                  type="text"
                  v-model="lname"
                  :placeholder="$t('forms.fields.lastName')"
                />
                <p v-if="isError('lname')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
              </div>
              <!-- .formRow__field__grid__column -->
            </div>
            <!-- .formRow__field__grid -->
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label" for="buy-email">{{ $t('forms.fields.email') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field">
            <input
              id="buy-email"
              class="form-textField"
              :class="{ 'form-textField--error': isError('email') }"
              type="email"
              v-model="email"
            />
            <p v-if="isError('email')" class="form-fieldError">{{ $t('forms.errors.validEmail') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label" for="buy-zip">{{ $t('forms.fields.zip') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field">
            <input
              id="buy-zip"
              class="form-textField"
              :class="{ 'form-textField--error': isError('zip') }"
              type="text"
              v-model="zip"
              @change="fetchAddress()"
            />
            <p v-if="isError('zip')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label" for="buy-houseNumber">{{ $t('forms.fields.houseNumber') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field">
            <div class="formRow__field__grid">
              <input
                id="leaseCalculator-houseNumber"
                class="form-textField formRow__field__grid__column"
                :class="{ 'form-textField--error': isError('houseNumber') }"
                type="text"
                @change="fetchAddress()"
                v-model="houseNumber"
              />

              <input
                id="leaseCalculator-houseNumberSupplement"
                class="form-textField formRow__field__grid__column"
                :class="{ 'form-textField--error': isError('houseNumberSupplement') }"
                type="text"
                :placeholder="$t('forms.fields.houseNumberSupplement')"
                v-model="houseNumberSupplement"
              />
            </div>
            <!-- .formRow__field__grid -->
            <p v-if="isError('houseNumber')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div v-if="addressError && !manualAddress" class="formRow">
          <div class="formRow__field formRow__field--noLabel">
            <p class="form-fieldWarning form-fieldWarning--noMargin">
              {{ $t('forms.errors.noAddressFound.text') }}
              <button type="button" class="button button--anchor" @click.prevent="fillManuallAddress()">
                {{ $t('forms.errors.noAddressFound.button') }}
              </button>
            </p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div v-if="street || manualAddress" class="formRow" :class="{ 'formRow--compact': !manualAddress }">
          <div class="formRow__label">
            <label class="form-label" for="buy-street">{{ $t('forms.fields.street') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field" :class="{ 'formRow__field--text': !manualAddress }">
            <span v-if="!manualAddress">{{ street }}</span>
            <input
              v-if="manualAddress"
              id="buy-street"
              class="form-textField"
              :class="{ 'form-textField--error': isError('street') }"
              type="text"
              v-model="street"
            />
            <p v-if="isError('street')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div v-if="city || manualAddress" class="formRow">
          <div class="formRow__label">
            <label class="form-label" for="buy-city">{{ $t('forms.fields.city') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field" :class="{ 'formRow__field--text': !manualAddress }">
            <span v-if="!manualAddress">{{ city }}</span>
            <input
              v-if="manualAddress"
              id="buy-city"
              class="form-textField"
              :class="{ 'form-textField--error': isError('city') }"
              type="text"
              v-model="city"
            />
            <p v-if="isError('city')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label" for="buy-phone">{{ $t('forms.fields.phone') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field">
            <input
              id="buy-phone"
              class="form-textField"
              :class="{ 'form-textField--error': isError('phone') }"
              type="text"
              v-model="phone"
            />
            <p v-if="isError('phone')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label">{{ $t('forms.buy.ownerQuestion') }}</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            <div class="form-radio form-radio--inline">
              <input id="buy-differentOwner-1" class="form-radio__input" type="radio" :value="false" v-model="differentOwner" />
              <label class="form-radio__label" for="buy-differentOwner-1">{{ $t('forms.buy.ownerSelf') }}</label>
            </div>
            <!-- .form-radio -->

            <div class="form-radio form-radio--inline">
              <input id="buy-differentOwner-2" class="form-radio__input" type="radio" :value="true" v-model="differentOwner" />
              <label class="form-radio__label" for="buy-differentOwner-2">{{ $t('forms.buy.ownerOther') }}</label>
            </div>
            <!-- .form-radio -->
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formGroup formGroup--pushBottom" v-if="differentOwner">
          <div class="formRow__heading">
            <span class="formRow__heading__title">{{ $t('forms.buy.ownerDetails') }}</span>
          </div>
          <!-- .formRow__heading -->
          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label">{{ $t('forms.fields.salutation') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--radio">
              <div class="form-radio form-radio--inline">
                <input id="buy-salutation2-1" class="form-radio__input" type="radio" value="Dhr." v-model="salutation2" />
                <label class="form-radio__label" for="buy-salutation2-1">{{ $t('forms.fields.salutationMale') }}</label>
              </div>
              <!-- .form-radio -->

              <div class="form-radio form-radio--inline">
                <input id="buy-salutation2-2" class="form-radio__input" type="radio" value="Mevr." v-model="salutation2" />
                <label class="form-radio__label" for="buy-salutation2-2">{{ $t('forms.fields.salutationFemale') }}</label>
              </div>
              <!-- .form-radio -->

              <p v-if="isError('salutation2')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="buy-fname2">{{ $t('forms.fields.name') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <div class="formRow__field__grid">
                <div class="formRow__field__grid__column">
                  <input
                    id="buy-fname2"
                    class="form-textField"
                    :class="{ 'form-textField--error': isError('fname2') }"
                    type="text"
                    v-model="fname2"
                    :placeholder="$t('forms.fields.firstName')"
                  />
                  <p v-if="isError('fname2')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
                </div>
                <!-- .formRow__field__grid__column -->
                <div class="formRow__field__grid__column">
                  <input
                    id="buy-lname2"
                    class="form-textField"
                    :class="{ 'form-textField--error': isError('lname2') }"
                    type="text"
                    v-model="lname2"
                    :placeholder="$t('forms.fields.lastName')"
                  />
                  <p v-if="isError('lname2')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
                </div>
                <!-- .formRow__field__grid__column -->
              </div>
              <!-- .formRow__field__grid -->
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="buy-email2">{{ $t('forms.fields.email') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input
                id="buy-email2"
                class="form-textField"
                :class="{ 'form-textField--error': isError('email2') }"
                type="email2"
                v-model="email2"
              />
              <p v-if="isError('email2')" class="form-fieldError">{{ $t('forms.errors.validEmail') }}</p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="buy-zip2">{{ $t('forms.fields.zip') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input
                id="buy-zip2"
                class="form-textField"
                :class="{ 'form-textField--error': isError('zip2') }"
                type="text"
                v-model="zip2"
                @change="fetchAddress(2)"
              />
              <p v-if="isError('zip2')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="buy-houseNumber2">{{ $t('forms.fields.houseNumber') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <div class="formRow__field__grid">
                <input
                  id="leaseCalculator-houseNumber2"
                  class="form-textField formRow__field__grid__column"
                  :class="{ 'form-textField--error': isError('houseNumber2') }"
                  type="text"
                  @change="fetchAddress(2)"
                  v-model="houseNumber2"
                />

                <input
                  id="leaseCalculator-houseNumberSupplement2"
                  class="form-textField formRow__field__grid__column"
                  :class="{ 'form-textField--error': isError('houseNumberSupplement2') }"
                  type="text"
                  :placeholder="$t('forms.fields.houseNumberSupplement')"
                  v-model="houseNumberSupplement2"
                />
              </div>
              <!-- .formRow__field__grid -->
              <p v-if="isError('houseNumber2')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div v-if="addressError2 && !manualAddress2" class="formRow">
            <div class="formRow__field formRow__field--noLabel">
              <p class="form-fieldWarning form-fieldWarning--noMargin">
                {{ $t('forms.errors.noAddressFound.text') }}
                <button type="button" class="button button--anchor" @click.prevent="fillManuallAddress(2)">
                  {{ $t('forms.errors.noAddressFound.button') }}
                </button>
              </p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div v-if="street2 || manualAddress2" class="formRow" :class="{ 'formRow--compact': !manualAddress2 }">
            <div class="formRow__label">
              <label class="form-label" for="buy-street2">{{ $t('forms.fields.street') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field" :class="{ 'formRow__field--text': !manualAddress2 }">
              <span v-if="!manualAddress2">{{ street2 }}</span>
              <input
                v-if="manualAddress2"
                id="buy-street2"
                class="form-textField"
                :class="{ 'form-textField--error': isError('street2') }"
                type="text"
                v-model="street2"
              />
              <p v-if="isError('street2')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div v-if="city2 || manualAddress2" class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="buy-city2">{{ $t('forms.fields.city') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field" :class="{ 'formRow__field--text': !manualAddress2 }">
              <span v-if="!manualAddress2">{{ city2 }}</span>
              <input
                v-if="manualAddress2"
                id="buy-city2"
                class="form-textField"
                :class="{ 'form-textField--error': isError('city') }"
                type="text"
                v-model="city2"
              />
              <p v-if="isError('city2')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="buy-phone2">{{ $t('forms.fields.phone') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input
                id="buy-phone2"
                class="form-textField"
                :class="{ 'form-textField--error': isError('phone2') }"
                type="text"
                v-model="phone2"
              />
              <p v-if="isError('phone2')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
        </div>
        <!-- .formGroup -->

        <div class="formRow">
          <div class="formRow__label">
            <label class="form-label">{{ $t('forms.buy.driverLicenseCountry') }}*</label>
          </div>
          <!-- .formRow__label -->
          <div class="formRow__field formRow__field--text">
            <div class="form-radio form-radio--inline">
              <input id="buy-hasDutchLicense-1" class="form-radio__input" type="radio" :value="true" v-model="hasDutchLicense" />
              <label class="form-radio__label" for="buy-hasDutchLicense-1">{{ $t('forms.buy.dutch') }}</label>
            </div>
            <!-- .form-radio -->

            <div class="form-radio form-radio--inline">
              <input id="buy-hasDutchLicense-2" class="form-radio__input" type="radio" :value="false" v-model="hasDutchLicense" />
              <label class="form-radio__label" for="buy-hasDutchLicense-2">{{ $t('forms.buy.foreign') }}</label>
            </div>
            <!-- .form-radio -->
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formGroup" v-if="hasDutchLicense">
          <div class="formRow__heading">
            <p class="formRow__heading__desc formRow__heading__desc--only">
              {{ $t('forms.buy.driverLicenseDisclaimer') }}
            </p>
          </div>
          <!-- .formRow__heading -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="buy-licenseNumber">{{ $t('forms.buy.driverLicenseNumber') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <input id="buy-licenseNumber" class="form-textField" type="licenseNumber" v-model="licenseNumber" />
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->

          <div class="formRow">
            <div class="formRow__label">
              <label class="form-label" for="buy-birthDate">{{ $t('forms.fields.birthDate') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <datepicker
                id="buy-birthDate"
                class="form-dateField"
                :language="datepickerLanguage"
                :disabled-dates="{ to: new Date(1900, 1, 1), from: new Date() }"
                :monday-first="true"
                :typeable="true"
                placeholder="dd/mm/yyyy"
                :format="customFormatter"
                :parser="customParser"
                v-model="birthDate"
              ></datepicker>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
        </div>

        <div class="formRow formRow--spaced">
          <div class="formRow__field formRow__field--noLabel">
            <div class="form-checkbox">
              <input id="buy-acceptTerms" class="form-checkbox__input" type="checkbox" v-model="acceptTerms" />
              <label class="form-checkbox__label" for="buy-acceptTerms">
                <span
                  >{{ $t('forms.fields.acceptTermsPre') }} <a :href="termsUrl" target="_blank">{{ $t('forms.fields.terms') }}</a></span
                >
              </label>
              <p v-if="isError('acceptTerms')" class="form-fieldError form-fieldError--spaced">
                {{ $t('forms.errors.acceptTerms') }}
              </p>
            </div>
            <!-- .form-checkbox -->
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__field formRow__field--noLabel">
            <button type="button" @click.prevent="toStep(2)" class="button button--primary button--arrow button--submit">
              {{ $t('forms.buy.toPaymentDetails') }}
            </button>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
      </div>
      <!-- .buyModal__page -->

      <div v-if="currentStep === 2" class="buyModal__page page-2">
        <p v-if="errors[2].length > 0" class="form-error">{{ $t('forms.errors.requiredFields') }}</p>

        <div class="formRow__heading">
          <span class="formRow__heading__title">{{ $t('forms.buy.paymentDetails') }}</span>
          <p class="buyModal__page__description">{{ $t('forms.buy.paymentDetailsDesc') }}</p>
        </div>
        <!-- .formRow__heading -->

        <div class="formRow">
          <div class="formRow__field formRow__field--text">
            <div class="form-radio">
              <input
                id="buy-paymentMethod-1"
                class="form-radio__input"
                type="radio"
                value="Betalen bij aflevering"
                v-model="paymentMethod"
              />
              <label class="form-radio__label" for="buy-paymentMethod-1">
                <span>
                  <strong>{{ $t('forms.buy.paymentAtDelivery') }}</strong
                  ><br />
                  {{ $t('forms.buy.paymentAtDeliveryDesc') }}
                </span>
              </label>
            </div>
            <!-- .form-radio -->

            <div class="form-radio">
              <input id="buy-paymentMethod-2" class="form-radio__input" type="radio" value="Betaalverzoek" v-model="paymentMethod" />
              <label class="form-radio__label" for="buy-paymentMethod-2">
                <span>
                  <strong>{{ $t('forms.buy.ideal') }}</strong
                  ><br />
                  {{ $t('forms.buy.idealDesc') }}
                </span>
              </label>
            </div>
            <!-- .form-radio -->

            <div class="form-radio">
              <input id="buy-paymentMethod-3" class="form-radio__input" type="radio" value="Bankoverschrijving" v-model="paymentMethod" />
              <label class="form-radio__label" for="buy-paymentMethod-3">
                <span>
                  <strong>{{ $t('forms.buy.bankTransfer') }}</strong
                  ><br />
                  {{ $t('forms.buy.bankTransferDesc') }}
                </span>
              </label>
            </div>
            <!-- .form-radio -->

            <p v-if="isError('paymentMethod')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow__heading" v-if="getPackagesInLanguage.length > 0 && useDeliveryPackages">
          <span class="formRow__heading__title">{{ $t('forms.buy.deliveryPackages') }}</span>
          <p class="formRow__heading__desc">{{ $t('forms.buy.deliveryPackagesDesc') }}</p>
        </div>
        <!-- .formRow__heading -->

        <div class="formRow" v-if="getPackagesInLanguage.length > 0 && useDeliveryPackages">
          <div class="formRow__field formRow__field--text">
            <div v-for="pack in getPackagesInLanguage" :key="pack.delivery_name" class="form-radio">
              <input
                :id="pack.delivery_name"
                type="radio"
                class="form-radio__input"
                :value="pack.delivery_name"
                v-model="deliveryPackage"
              />
              <label :for="pack.delivery_name" class="form-radio__label">
                <div class="option-list-extended">
                  <div class="option-list-extended__heading">
                    <h4>{{ pack.delivery_name }}</h4>
                    <div v-if="pack.delivery_price" class="option-list-extended__price">
                      {{ pack.delivery_price }}
                    </div>
                  </div>
                  <p v-if="pack.delivery_desc">
                    {{ pack.delivery_desc }}
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div v-if="options.length > 0" class="formRow__heading">
          <span class="formRow__heading__title">{{ $t('forms.buy.extraOptions') }}</span>
          <p class="formRow__heading__desc">{{ $t('forms.buy.extraOptionsDesc') }}</p>
        </div>
        <!-- .formRow__heading -->

        <div v-if="options.length > 0" class="formRow">
          <div class="formRow__field formRow__field--text">
            <div v-for="option in options" :key="option.name" class="form-checkbox">
              <input
                :id="`buy-${option.name}`"
                :name="`buy-${option.name}`"
                class="form-checkbox__input"
                type="checkbox"
                :value="option.name"
                checked
                v-model="deliveryOptions"
              />
              <label class="form-checkbox__label" :for="`buy-${option.name}`">
                <strong>{{ $t(`forms.buy.${option.name}`) }}</strong>
                {{ option.price ? `&nbsp;(&euro; ${option.price})` : null }}
              </label>
            </div>
            <!-- .form-checkbox -->

            <p v-if="isError('deliveryMethod')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->

        <div class="formRow">
          <div class="formRow__field">
            <button v-on:click="toStep(3)" class="button button--primary button--arrow button--submit">
              {{ $t('forms.buy.toConfirm') }}
            </button>
          </div>
          <!-- .formRow__field -->
        </div>
        <!-- .formRow -->
      </div>
      <!-- .buyModal__page -->

      <div v-if="currentStep === 3" class="buyModal__page page-3">
        <p class="buyModal__page__description">{{ $t('forms.buy.confirmDesc') }}</p>

        <p v-if="errorMsg" class="form-error">{{ errorMsg }}</p>

        <div class="formGroup formGroup--pushBottom">
          <div class="formRow__heading">
            <span class="formRow__heading__title">{{ $t('forms.buy.personalDetails') }}</span>
            <button class="button button--anchor" v-on:click="toStep(1)">({{ $t('forms.buy.edit') }})</button>
          </div>
          <!-- .formRow__heading -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.name') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ salutation === 'Dhr.' ? $t('forms.fields.salutationMale') : $t('forms.fields.salutationFemale') }}
              {{ fname }} {{ lname }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.email') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ email }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.address') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ street }} {{ houseNumber }}{{ houseNumberSupplement ? ' ' + houseNumberSupplement : '' }}, {{ zip }},
              {{ city }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.phone') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ phone }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div v-if="!differentOwner && hasDutchLicense" class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.buy.driverLicenseNumber') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ licenseNumber || $t('forms.fields.unknown') }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div v-if="!differentOwner && hasDutchLicense" class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.birthDate') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              <span v-if="birthDate">{{ birthDate | dateFormat('DD/MM/YYYY') }}</span>
              <span v-else>{{ $t('forms.fields.unknown') }}</span>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
        </div>
        <!-- .formGroup -->

        <div v-if="differentOwner" class="formGroup formGroup--pushBottom">
          <div class="formRow__heading">
            <span class="formRow__heading__title">{{ $t('forms.buy.ownerDetails') }}</span>
            <button type="button" class="button button--anchor" @click.prevent="toStep(1)">({{ $t('forms.buy.edit') }})</button>
          </div>
          <!-- .formRow__heading -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.name') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ salutation2 === 'Dhr.' ? $t('forms.fields.salutationMale') : $t('forms.fields.salutationFemale') }}
              {{ fname2 }} {{ lname2 }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.email') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ email2 }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.address') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ street2 }} {{ houseNumber2 }}{{ houseNumberSupplement2 ? ' ' + houseNumberSupplement2 : '' }}, {{ zip2 }}, {{ city2 }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.phone') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ phone2 }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div v-if="hasDutchLicense" class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.buy.driverLicenseNumber') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ licenseNumber || $t('forms.fields.unknown') }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div v-if="hasDutchLicense" class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.fields.birthDate') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              <span v-if="birthDate">{{ birthDate | dateFormat('DD/MM/YYYY') }}</span>
              <span v-else>{{ $t('forms.fields.unknown') }}</span>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
        </div>
        <!-- .formGroup -->

        <div class="formGroup">
          <div class="formRow__heading">
            <span class="formRow__heading__title">{{ $t('forms.buy.stepPaymentDelivery') }}</span>
            <button type="button" class="button button--anchor" @click.prevent="toStep(2)">({{ $t('forms.buy.edit') }})</button>
          </div>
          <!-- .formRow__heading -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.buy.paymentDetails') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              <span v-if="paymentMethod === 'Bankoverschrijving'">{{ $t('forms.buy.bankTransfer') }}</span>
              <span v-else-if="paymentMethod === 'Betaalverzoek'">{{ $t('forms.buy.ideal') }}</span>
              <span v-else-if="paymentMethod === 'Betalen bij aflevering'">{{ $t('forms.buy.paymentAtDelivery') }}</span>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div class="formRow formRow--compact" v-if="getPackagesInLanguage.length > 0 && useDeliveryPackages">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.buy.deliveryPackages') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ deliveryPackage ? deliveryPackage : $t('forms.fields.unknown') }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
          <div class="formRow formRow--compact">
            <div class="formRow__label">
              <span class="form-label">{{ $t('forms.buy.extraOptions') }}</span>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field formRow__field--text">
              {{ deliveryOptions.length > 0 ? listExtraOptions.join(', ') : $t('forms.fields.unknown') }}
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
        </div>
        <!-- .formGroup -->

        <div class="formGroup formGroup--transparent formGroup--pushBottom">
          <div class="formRow formRow--labelTop">
            <div class="formRow__label formRow__label--top">
              <label class="form-label" for="offer-comments">{{ $t('forms.fields.comments') }}</label>
            </div>
            <!-- .formRow__label -->
            <div class="formRow__field">
              <textarea id="offer-comments" class="form-textarea form-textarea--large" v-model="comments"></textarea>
            </div>
            <!-- .formRow__field -->
          </div>
          <!-- .formRow -->
        </div>
        <!-- .formGroup -->

        <button
          type="submit"
          class="button button--primary button--arrow button--submit button--alignRight"
          :class="{ 'button--loading': loading }"
          :disabled="loading"
        >
          {{ $t('forms.fields.submitRequest') }}
        </button>
      </div>
      <!-- .buyModal__page -->
      <div v-if="currentStep === 4" class="buyModal__page page-4">
        <p v-if="successMsg" class="form-success">{{ successMsg }}</p>
      </div>
      <!-- .buyModal__page -->
    </form>
  </Modal>
</template>

<style lang="scss">
.imot.buyModal {
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  justify-content: center;

  .buyModal {
    &__loadingWrapper {
      min-height: 50px;
    }

    &__loading {
      background: url('../../assets/icons/loading-spinner.svg') center center no-repeat;
      background-size: 150% auto;
      width: 40px;
      height: 40px;
    }

    &__page {
      &__description {
        font-size: 15px;
        margin: 0 0 1em 0;
      }
    }
  }

  .formRow__heading__title {
    display: inline-block;
    margin-bottom: 0.25em;
  }

  .form-modal {
    position: relative;
    transform: none;
    top: 0;
    left: 0;
  }

  .resultText {
    font-size: 20px;
    font-weight: bold;
    color: #000;

    &__after {
      font-size: 14px;
      vertical-align: 2px;
      display: inline-block;
      margin-left: 0.25em;
    }
  }
}
</style>

<script>
import Modal from '@/components/Modal'
import { WPSettings } from '@/WPSettings'
import axios from 'axios'
import Datepicker from '@sum.cumo/vue-datepicker'
import '@sum.cumo/vue-datepicker/dist/Datepicker.css'
import { nl, en, de, fr } from '@sum.cumo/vue-datepicker/dist/locale/index.esm'
import { format, parse } from 'date-fns'

export default {
  name: 'modalBuy',
  data() {
    return {
      WPSettings: WPSettings,
      errors: { 1: [], 2: [] },
      loading: false,
      errorMsg: '',
      successMsg: '',
      currentStep: 1,
      generalSettings: this.$store.state.settings.general,

      // Address fetch
      debounceTimer: null,
      addressError: false,
      manualAddress: false,
      debounceTimer2: null,
      addressError2: false,
      manualAddress2: false,

      // Form
      orderType: 'Particulier',
      salutation: '',
      fname: '',
      lname: '',
      email: '',
      zip: '',
      street: '',
      houseNumber: '',
      houseNumberSupplement: '',
      city: '',
      phone: '',
      paymentMethod: '',
      deliveryOptions: [],
      deliveryPackage: '',
      preferredDeliveryDate: '',
      comments: '',
      differentOwner: false,
      salutation2: '',
      fname2: '',
      lname2: '',
      email2: '',
      zip2: '',
      street2: '',
      houseNumber2: '',
      houseNumberSupplement2: '',
      city2: '',
      phone2: '',
      hasDutchLicense: null,
      licenseNumber: '',
      birthDate: '',

      acceptTerms: false,
      termsUrl: this.$store.state.settings.general.termsUrl,
    }
  },
  props: ['active', 'packages', 'options', 'introText', 'vehicle', 'useDeliveryPackages'],
  components: {
    Modal,
    Datepicker,
  },
  computed: {
    currentVehicleId() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle ? currentVehicle.id : null
    },
    getPackagesInLanguage() {
      const output = []

      this.packages.forEach((item) => {
        const pack = this.createDeliveryPackage(item)
        output.push(pack)
      })
      if (output.length > 0) {
        this.deliveryPackage = output[0]['delivery_name']
      }
      return output
    },
    listExtraOptions() {
      const options = []
      this.deliveryOptions.forEach((opt) => {
        options.push(this.$t(`forms.buy.${opt}`))
      })
      console.log(options)
      return options
    },
    datepickerLanguage() {
      switch (WPSettings.LANGUAGE) {
        case 'en':
          return en
        case 'fr':
          return fr
        case 'de':
          return de
        default:
          return nl
      }
    },
  },
  methods: {
    createDeliveryPackage(item) {
      const output = {
        delivery_price: item.delivery_price,
        delivery_name: item.delivery_name,
        delivery_desc: item.delivery_desc,
      }
      if (item[`delivery_name_${WPSettings.LANGUAGE}`]) {
        output.delivery_name = item[`delivery_name_${WPSettings.LANGUAGE}`]
      }
      if (item[`delivery_desc_${WPSettings.LANGUAGE}`]) {
        output.delivery_desc = item[`delivery_desc_${WPSettings.LANGUAGE}`]
      }
      return output
    },
    customFormatter(date) {
      return format(date, 'dd/MM/yyyy')
    },
    customParser(date) {
      date = date.replace(/\//g, '-')
      return parse(date, 'dd-MM-yyyy', new Date())
    },
    toStep(step, onlyBack = false) {
      if (step > this.currentStep) {
        if (onlyBack) {
          return
        } else if (step > 1) {
          this.validateForm()

          if (this.errors[this.currentStep].length) {
            return
          }
        }
      }
      this.currentStep = step
    },
    validateForm() {
      // Reset errors
      this.errors[this.currentStep] = []

      // Fields to check
      let checkIfHasValue = []
      if (this.currentStep === 1) {
        checkIfHasValue = [
          'orderType',
          'salutation',
          'fname',
          'lname',
          'email',
          'zip',
          'street',
          'houseNumber',
          'city',
          'phone',
          'acceptTerms',
        ]

        if (this.differentOwner) {
          checkIfHasValue = checkIfHasValue.concat([
            'salutation2',
            'fname2',
            'lname2',
            'email2',
            'zip2',
            'street2',
            'houseNumber2',
            'city2',
            'phone2',
          ])
        }
      } else if (this.currentStep === 2) {
        checkIfHasValue = ['paymentMethod']
      }
      // Check fields
      checkIfHasValue.forEach((field) => {
        if (!this[field]) {
          // Field has no value, so add it to error list
          this.errors[this.currentStep].push(field)
        } else if (field === 'email' || field === 'email2') {
          // Test email formatting
          const regexp =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

          if (!regexp.test(this[field].toLowerCase().trim())) {
            this.errors[this.currentStep].push(field)
          }
        }
      })
    },
    isError(field) {
      // Check if field is in the error list
      return this.errors[this.currentStep].includes(field)
    },
    closeModal() {
      // Reset steps
      this.currentStep = 1

      // Emit to parent component
      this.$emit('modalClosed', true)

      // Reset errors - just because it's prettier
      this.errors = { 1: [], 2: [] }
    },
    submitForm(e) {
      // Prevent form from being submitted by browser
      e.preventDefault()

      // Do the call to WP REST
      this.loading = true

      axios
        .post(
          WPSettings.API_URL + '/form-buy',
          {
            vehicleId: this.currentVehicleId,
            acceptTerms: this.acceptTerms ? true : null,
            orderType: this.orderType,
            salutation: this.salutation,
            fname: this.fname,
            lname: this.lname,
            email: this.email,
            phoneNumber: this.phone,
            street: this.street,
            houseNumber: this.houseNumber,
            houseNumberSupplement: this.houseNumberSupplement,
            zip: this.zip,
            city: this.city,
            differentOwner: this.differentOwner,
            ownerSalutation: this.differentOwner ? this.salutation2 : null,
            ownerFname: this.differentOwner ? this.fname2 : null,
            ownerLname: this.differentOwner ? this.lname2 : null,
            ownerEmail: this.differentOwner ? this.email2 : null,
            ownerPhoneNumber: this.differentOwner ? this.phone2 : null,
            ownerStreet: this.differentOwner ? this.street2 : null,
            ownerHouseNumber: this.differentOwner ? this.houseNumber2 : null,
            ownerhouseNumberSupplement: this.differentOwner ? this.houseNumberSupplement2 : null,
            ownerZip: this.differentOwner ? this.zip2 : null,
            ownerCity: this.differentOwner ? this.city2 : null,
            licenseNumber: this.licenseNumber,
            birthdate: this.formatDateForApi(this.birthDate),
            paymentMethod: this.paymentMethod,
            deliveryPackage: this.deliveryPackage,
            deliveryOptions: this.deliveryOptions,
            preferredDeliveryDate: this.formatDateForApi(this.preferredDeliveryDate),
            comments: this.comments,
          },
          {
            headers:
              process.env.NODE_ENV === 'production'
                ? {
                    'X-WP-Nonce': WPSettings.NONCE,
                  }
                : {},
          }
        )
        .then((response) => {
          if (response.data.status === 'success') {
            // Update success message
            this.errorMsg = ''
            this.successMsg = response.data.message
            this.currentStep = 4

            // Reset form
            this.salutation =
              this.fname =
              this.lname =
              this.email =
              this.phoneNumber =
              this.street =
              this.houseNumber =
              this.zip =
              this.city =
              this.paymentMethod =
              this.deliveryMethod =
              this.comments =
              this.acceptTerms =
                ''
            this.orderType = 'Particulier'
          }
        })
        .catch((error) => {
          if (error.response.data.status === 'error') {
            // Update error message
            this.successMsg = ''
            this.errorMsg = error.response.data.message
          } else {
            this.successMsg = ''
            this.errorMsg = this.$t('forms.errors.somethingWentWrong')
          }
        })
        .finally(() => (this.loading = false))
    },
    fetchAddress(fieldIndex = '') {
      // Format field values
      if (this['zip' + fieldIndex]) this['zip' + fieldIndex] = this['zip' + fieldIndex].replace(' ', '').toUpperCase()
      if (this['houseNumber' + fieldIndex]) {
        const convertHouseNumber = parseInt(this['houseNumber' + fieldIndex])
        this['houseNumber' + fieldIndex] = !isNaN(convertHouseNumber) ? convertHouseNumber : ''
      }

      // Check if values exist
      if (!this['zip' + fieldIndex] || !this['houseNumber' + fieldIndex]) {
        return
      }

      // Clear current running timer
      clearTimeout(this['debounceTimer' + fieldIndex])

      // Start a new debounce timer
      this['debounceTimer' + fieldIndex] = setTimeout(() => {
        axios
          .get(
            WPSettings.API_URL + '/get-address',
            {
              params: {
                number: this['houseNumber' + fieldIndex],
                zip: this['zip' + fieldIndex],
              },
            },
            {
              headers:
                process.env.NODE_ENV === 'production'
                  ? {
                      'X-WP-Nonce': WPSettings.NONCE,
                    }
                  : {},
            }
          )
          .then((response) => {
            this['city' + fieldIndex] = response.data.city
            this['street' + fieldIndex] = response.data.street
            this['manualAddress' + fieldIndex] = false
            this['addressError' + fieldIndex] = false

            // Remove from error list
            this.errors[this.currentStep] = this.errors[this.currentStep].filter(
              (item) => item !== 'city' + fieldIndex && item !== 'street' + fieldIndex
            )
          })
          .catch(() => {
            this['addressError' + fieldIndex] = true
            this['city' + fieldIndex] = this['street' + fieldIndex] = ''
          })
      }, 500)
    },
    fillManuallAddress(fieldIndex = '') {
      this['manualAddress' + fieldIndex] = true
    },
    formatDateForApi(date) {
      if (!date) {
        return null
      }

      return format(date, 'dd/MM/yyyy')
    },
  },
}
</script>
